import { ReactElement, useCallback } from "react";
import {
	FormItem,
	IconButton,
	InputRow,
	TextInput,
	Toggle,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { IValueInputAttachable } from "client/Forms/attachable";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";
import { HostFormModel } from "../common";
import { getClientId, setClientId } from "client/Misc/clientId";
import { useCallbackWithoutArguments } from "client/Hooks";
import styles from "./RestrictionSettings.module.less";

export type RestrictionSettingsProps = {
	onToggle: (value: boolean) => void;
	attachable: IValueInputAttachable<OptionalFormDataModel<readonly HostFormModel[]>>;
};

export const RestrictionSettings = (props: RestrictionSettingsProps): ReactElement => {
	const {
		onToggle,
		attachable
	} = props;

	const isSetAttachable: IValueInputAttachable<boolean> = attachable.for("isSet");

	const { isSet } = attachable.value;
	const toggleText: string = localize("_RestrictSendingAuthenticationDataInfo");

	return (
		<>
			<FormItem label={localize("_Security")}>
				<Toggle
					{...isSetAttachable.attachToCheckbox()
						.withOnChange(onToggle)
					}
					text={toggleText}
					tooltip={toggleText}
				/>
				<ValidationError {...isSetAttachable.attachToValidationError()} />
			</FormItem>

			{isSet && (
				<Hosts
					attachable={attachable.for("value")}
				/>
			)}
		</>
	);
};
RestrictionSettings.displayName = "RestrictionSettings";

type HostsProps = {
	attachable: IValueInputAttachable<readonly HostFormModel[]>;
};

const Hosts = (props: HostsProps): ReactElement => {
	const { attachable } = props;

	const trackingId: string = attachable
		.reducer
		.path;

	const count: number = attachable
		.value
		.length;

	const addHandler = useCallback(
		(): void => {
			const newHost: HostFormModel = setClientId({
				value: ""
			});

			attachable
				.reducer
				.push(newHost);

			attachable
				.validation
				.triggerInputFinished();
		},
		[
			attachable.reducer,
			attachable.validation
		]
	);

	const removeHandler = useCallback(
		(index: number): void => {
			attachable
				.reducer
				.removeAt(index);

			attachable
				.validation
				.triggerInputFinished();
		},
		[
			attachable.reducer,
			attachable.validation
		]
	);

	return (
		<FormItem
			label={{
				template: localize("_DomainsMax"),
				replacements: [
					{
						what: "{{MaxCount}}",
						filler: `${MAX_HOST_COUNT}`
					}
				]
			}}
		>
			<ul
				{...setTrackingId(trackingId)}
				className={styles.list}
			>
				{attachable
					.map(
						(x, i) => (
							<li
								key={getClientId(x.value)}
								className={styles.item}
							>
								<HostEntry
									attachable={x.for("value")}
									canBeDeleted={count > MIN_HOST_COUNT}
									onRemove={() => removeHandler(i)}
								/>
							</li>
						)
					)
				}
			</ul>

			{count < MAX_HOST_COUNT && (
				<IconButton
					kind="dashed"
					width="fill"
					icon="plus"
					onClick={addHandler}
					trackingId={generateTrackingId(trackingId, "__add")}
				/>
			)}

			<ValidationError {...attachable.attachToValidationError(true)} />
		</FormItem>
	);
};
Hosts.displayName = "Hosts";

export const MIN_HOST_COUNT: number = 1;
export const MAX_HOST_COUNT: number = 10;

type HostEntryProps = {
	attachable: IValueInputAttachable<string>;
	canBeDeleted: boolean;
	onRemove: () => void;
};

const HostEntry = (props: HostEntryProps): ReactElement => {
	const {
		attachable,
		canBeDeleted,
		onRemove: onRemoveFromProps
	} = props;

	const onRemove: () => void = useCallbackWithoutArguments(onRemoveFromProps);

	return (
		<>
			<InputRow>
				<InputRow.Stretch>
					<TextInput
						{...attachable.attachToTextInput()}
						placeholder="example.com"
					/>
				</InputRow.Stretch>

				<IconButton
					kind="secondary"
					icon="minus"
					onClick={onRemove}
					trackingId={generateTrackingId(
						attachable.reducer
							.path,
						"__remove"
					)}
					isDisabled={!canBeDeleted}
				/>
			</InputRow>

			<ValidationError {...attachable.attachToValidationError()} />
		</>
	);
};
HostEntry.displayName = "HostEntry";